import { useCallback, useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";

import { CompareArrows as CompareArrowsIcon, Search } from "@mui/icons-material";
import { IconButton, InputAdornment, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";

import { apiClient, formatNumber, getErrorMessage } from "../../shared";
import { CustomInputField, useSnackbar } from "../../ui";
import { WarehouseSelect } from "../../warehouses";
import { WarehouseStockTransactionsDialog } from "../../warehouse-stock-transactions/warehouse-stock-transactions-dialog";
import { ProductVariantSearchDialog } from "./product-variant-search-form-dialog";

export const WarehouseStockByProductVariants = ({
  initialSku = null,
  initialProductVariantName = null,
  initialWarehouseId = null,
  initialWarehouseName = null,
  onProductVariantChange,
  onWarehouseChange,
}) => {
  const { showSnackbar } = useSnackbar();
  // Full stock list for the selected variant. A variant lives in at most a
  // handful of warehouses, so we load them all and filter/paginate client-side
  // (keeps the optional warehouse filter correct without a second endpoint).
  const [stocks, setStocks] = useState([]);
  const [filters, setFilters] = useState({
    product: null,
    productVariant: null,
  });
  const [warehouseFilter, setWarehouseFilter] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // Warehouse whose transactions dialog is currently open (distinct from the
  // warehouse *filter* above).
  const [transactionsWarehouse, setTransactionsWarehouse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Seed the variant filter from a deep link (?sku=...). Only the SKU is
  // required to load stock; the name is display-only and falls back to the SKU.
  // Guarded so that when the URL is updated *from* a manual selection (same SKU)
  // we keep the richer selected object instead of clobbering it with a minimal
  // one.
  useEffect(() => {
    if (!initialSku) return;
    setFilters((prev) =>
      prev.productVariant?.sku === initialSku
        ? prev
        : {
            product: null,
            productVariant: {
              sku: initialSku,
              name: initialProductVariantName || initialSku,
            },
          }
    );
  }, [initialSku, initialProductVariantName]);

  // Seed the optional warehouse filter from a deep link (?warehouseId=...), with
  // the same anti-clobber guard as the variant filter above.
  useEffect(() => {
    if (!initialWarehouseId) return;
    setWarehouseFilter((prev) =>
      prev?.id === initialWarehouseId
        ? prev
        : { id: initialWarehouseId, name: initialWarehouseName || "" }
    );
  }, [initialWarehouseId, initialWarehouseName]);

  const loadStocksByProductVariants = useCallback(async () => {
    setLoading(true);
    try {
      const encodedSKU = encodeURIComponent(filters.productVariant.sku);

      const {
        data: { data },
      } = await apiClient.get(`product-variants/${encodedSKU}/stocks`, {
        params: {
          page: 0,
          size: 100,
        },
      });
      setStocks(data);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat stok produk varian"));
    } finally {
      setLoading(false);
    }
  }, [filters.productVariant, showSnackbar]);

  useEffect(() => {
    if (filters.productVariant) {
      loadStocksByProductVariants();
    } else {
      setStocks([]);
    }
  }, [loadStocksByProductVariants, filters.productVariant]);

  const displayedStocks = useMemo(
    () =>
      warehouseFilter
        ? stocks.filter((stock) => stock.warehouse?.id === warehouseFilter.id)
        : stocks,
    [stocks, warehouseFilter]
  );

  // Reset to the first page whenever the result set changes.
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [warehouseFilter, filters.productVariant]);

  const handleWarehouseFilterChange = (warehouse) => {
    setWarehouseFilter(warehouse);
    onWarehouseChange?.(warehouse);
  };

  return (
    <Stack spacing={3}>
      <Grid container spacing={2} sx={{ justifyContent: "flex-end" }}>
        <Grid size={{ xs: 12, md: 4, lg: 3 }}>
          <WarehouseSelect
            value={warehouseFilter}
            onChange={handleWarehouseFilterChange}
            TextFieldProps={{
              label: "Gudang",
              InputProps: {
                placeholder: "Filter gudang (opsional)",
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 6 }}>
          <CustomInputField
            label="Produk Varian"
            value={filters?.productVariant?.name || ""}
            onClick={() => setIsDialogOpen(true)}
            placeholder="Cari produk varian"
            fullWidth
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <DataGrid
        rows={displayedStocks}
        loading={loading}
        getRowHeight={() => "auto"}
        columns={[
          {
            field: "warehouseName",
            headerName: "Gudang",
            flex: 2,
            sortable: false,
            valueGetter: (_value, row) => row.warehouse.name,
          },
          {
            field: "address",
            headerName: "Alamat",
            flex: 3,
            sortable: false,
            valueGetter: (_value, row) =>
              row.warehouse.warehouseAddress.address || "-",
          },
          {
            field: "quantity",
            headerName: "Kuantitas",
            flex: 1,
            type: "number",
            valueFormatter: (value) => formatNumber(value),
            sortable: false,
          },
          {
            field: "actions",
            type: "actions",
            headerName: "Aksi",
            getActions: ({ row }) => [
              <IconButton
                key="transactions"
                onClick={() => setTransactionsWarehouse(row.warehouse)}
                size="small"
              >
                <CompareArrowsIcon />
              </IconButton>,
            ],
          },
        ]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 20, 50]}
        disableRowSelectionOnClick
      />

      {transactionsWarehouse && (
        <WarehouseStockTransactionsDialog
          warehouse={transactionsWarehouse}
          productVariant={filters.productVariant}
          onClose={() => setTransactionsWarehouse(null)}
        />
      )}

      {isDialogOpen && (
        <ProductVariantSearchDialog
          defaultValues={{
            product: filters?.product,
            productVariant: filters?.productVariant,
            variantAttribute1Value:
              filters?.productVariant?.variantAttribute1Value,
            variantAttribute2Value:
              filters?.productVariant?.variantAttribute2Value,
            variantAttribute3Value:
              filters?.productVariant?.variantAttribute3Value,
            variantAttribute4Value:
              filters?.productVariant?.variantAttribute4Value,
          }}
          onSubmit={(data) => {
            setFilters({
              product: data.product,
              productVariant: data.productVariant,
            });
            onProductVariantChange?.(data.productVariant);
          }}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </Stack>
  );
};

WarehouseStockByProductVariants.propTypes = {
  initialSku: PropTypes.string,
  initialProductVariantName: PropTypes.string,
  initialWarehouseId: PropTypes.string,
  initialWarehouseName: PropTypes.string,
  onProductVariantChange: PropTypes.func,
  onWarehouseChange: PropTypes.func,
};
